// Frontend logging utility
// Provides structured logging with different levels and environment-aware output

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// Determine current log level based on environment
const isDevelopment = process.env.NODE_ENV === 'development';
const CURRENT_LOG_LEVEL = isDevelopment ? LOG_LEVELS.DEBUG : LOG_LEVELS.INFO;

// Format timestamp
const formatTimestamp = () => {
  return new Date().toISOString();
};

// Sanitize sensitive data from context (tokens, passwords, etc.)
const sanitizeContext = (context) => {
  if (!context || typeof context !== 'object') {
    return context;
  }
  
  const sanitized = { ...context };
  const sensitiveKeys = [
    'token', 'Token', 'TOKEN',
    'password', 'Password', 'PASSWORD',
    'authorization', 'Authorization', 'AUTHORIZATION',
    'auth', 'Auth', 'AUTH',
    'secret', 'Secret', 'SECRET',
    'jwt', 'JWT',
    'bearer', 'Bearer', 'BEARER'
  ];
  
  // Remove sensitive keys
  sensitiveKeys.forEach(key => {
    if (sanitized[key] !== undefined) {
      delete sanitized[key];
    }
  });
  
  // Sanitize nested objects and arrays
  Object.keys(sanitized).forEach(key => {
    if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      if (Array.isArray(sanitized[key])) {
        sanitized[key] = sanitized[key].map(item => 
          typeof item === 'object' ? sanitizeContext(item) : item
        );
      } else {
        sanitized[key] = sanitizeContext(sanitized[key]);
      }
    }
    // Sanitize string values that might contain tokens
    if (typeof sanitized[key] === 'string' && sanitized[key].length > 20) {
      // Check if string looks like a JWT token (starts with eyJ)
      if (sanitized[key].startsWith('eyJ')) {
        sanitized[key] = '[REDACTED_TOKEN]';
      }
      // Check if string contains "Bearer" (Authorization header)
      if (sanitized[key].includes('Bearer ')) {
        sanitized[key] = '[REDACTED_AUTH_HEADER]';
      }
    }
  });
  
  return sanitized;
};

// Format log message
const formatMessage = (level, message, context = {}) => {
  const timestamp = formatTimestamp();
  const sanitizedContext = sanitizeContext(context);
  const contextStr = Object.keys(sanitizedContext).length > 0 
    ? ` ${JSON.stringify(sanitizedContext)}` 
    : '';
  
  if (isDevelopment) {
    // Development: human-readable format. No emoji — console already colors levels.
    return `${timestamp} [${level}] ${message}${contextStr}`;
  } else {
    // Production: JSON format for log aggregation
    return JSON.stringify({
      timestamp,
      level,
      message,
      ...sanitizedContext
    });
  }
};

// Logger object
const logger = {
  debug(message, context = {}) {
    if (CURRENT_LOG_LEVEL <= LOG_LEVELS.DEBUG) {
      console.log(formatMessage('DEBUG', message, context));
    }
  },

  info(message, context = {}) {
    if (CURRENT_LOG_LEVEL <= LOG_LEVELS.INFO) {
      console.log(formatMessage('INFO', message, context));
    }
  },

  warn(message, context = {}) {
    if (CURRENT_LOG_LEVEL <= LOG_LEVELS.WARN) {
      console.warn(formatMessage('WARN', message, context));
    }
  },

  error(message, error = null, context = {}) {
    if (CURRENT_LOG_LEVEL <= LOG_LEVELS.ERROR) {
      const errorContext = {
        ...context,
      };
      
      if (error) {
        errorContext.error = {
          message: error.message,
          stack: error.stack,
          name: error.name,
        };
      }
      
      console.error(formatMessage('ERROR', message, errorContext));
    }
  },

  // API request logging
  api(method, url, status = null, context = {}) {
    const apiContext = {
      method,
      url,
      ...context,
    };
    
    if (status !== null) {
      apiContext.status = status;
      const level = status >= 500 ? 'ERROR' : 
                    status >= 400 ? 'WARN' : 'INFO';
      this[level.toLowerCase()](`API ${method} ${url}`, apiContext);
    } else {
      this.debug(`API ${method} ${url}`, apiContext);
    }
  },
};

export default logger;

